import React from "react";
import { Link } from "react-router-dom";
import { SUBPROCESSORS } from "config/trustCenterContent";

const PRIVACY_POLICY_LAST_UPDATED = "September 1, 2026";
const RETENTION_MONTHS_AFTER_CANCELLATION = 12;

export { PRIVACY_POLICY_LAST_UPDATED };

export default function PrivacyPolicyContent() {
  return (
    <article className="privacy-policy-content">
      <h1>Privacy Policy</h1>
      <p className="text-muted">Last updated: {PRIVACY_POLICY_LAST_UPDATED}</p>

      <p>
        This Privacy Policy describes how Lesson Launchpad™ (&quot;Lesson Launchpad,&quot; &quot;we,&quot;
        &quot;us,&quot; or &quot;our&quot;) collects, uses, shares, and protects information when you use
        our website and classroom automation service at{" "}
        <a href="https://lessonlaunchpad.com">https://lessonlaunchpad.com</a> (the &quot;Service&quot;).
      </p>
      <p>
        Lesson Launchpad is designed for educators and schools. By using the Service, you agree to this Privacy
        Policy. If you do not agree, please do not use the Service.
      </p>
      <p>
        For a summary of our security and compliance practices, see our{" "}
        <Link to="/trust">Trust Center</Link>.
      </p>

      <h2 className="h4 mt-4">1. Who We Are</h2>
      <p>
        Lesson Launchpad is operated by Lesson Launchpad LLC, based in North Carolina, United States. Questions
        about privacy or security can be sent to{" "}
        <a href="mailto:support@lessonlaunchpad.com">support@lessonlaunchpad.com</a> or{" "}
        <a href="mailto:security@lessonlaunchpad.com">security@lessonlaunchpad.com</a>.
      </p>

      <h2 className="h4 mt-4">2. Information We Collect</h2>

      <h3 className="h5 mt-3">Account and teacher information</h3>
      <p>When you create or use a teacher account, we may collect:</p>
      <ul>
        <li>Name and email address</li>
        <li>Password or authentication credentials (stored through Firebase Authentication)</li>
        <li>Teaching profile details you choose to provide, such as school type, grade levels, and subjects taught</li>
        <li>School affiliation, school name, or school identifier when you connect to a school license or update your profile</li>
        <li>Subscription, trial, referral, and billing status</li>
        <li>How you heard about Lesson Launchpad (lead source) and marketing attribution data collected at signup</li>
        <li>Settings, preferences, and product-tour progress</li>
      </ul>

      <h3 className="h5 mt-3">Student and classroom information</h3>
      <p>
        Lesson Launchpad is not designed for direct student signup. Teachers and authorized school staff enter
        classroom information to use presentation, automation, and planning features. Depending on how you use
        the Service, this may include:
      </p>
      <ul>
        <li>Student first and last names in class rosters</li>
        <li>Student birthdays when you enable birthday-related automations</li>
        <li>Class schedules, agendas, reminders, countdowns, and related classroom configuration</li>
        <li>Lesson content, notes, images, files, and other materials you create or upload for classroom use</li>
        <li>Student-generated or classroom-facing content you choose to store in the Service</li>
        <li>Share-link settings that allow read-only access to a Present screen view</li>
      </ul>
      <p>
        We do <strong>not</strong> intentionally collect social security numbers, medical or health records, IEP or
        special education records, academic grades or report card data, ethnicity or race, bus or transportation
        assignments, or parent or guardian phone numbers through our standard product workflows.
      </p>

      <h3 className="h5 mt-3">Billing, school procurement, and support information</h3>
      <p>If you purchase a subscription, request a school quote, or contact us, we may collect:</p>
      <ul>
        <li>Billing contact information and payment-related metadata processed by Stripe</li>
        <li>School or district name, purchase order details, and procurement contact information</li>
        <li>Messages, testimonials, and other information you submit through contact or support forms</li>
        <li>Device and browser diagnostic metadata submitted with support requests to help troubleshoot issues</li>
      </ul>

      <h3 className="h5 mt-3">Usage, device, and analytics information</h3>
      <p>We automatically collect certain technical and usage information, such as:</p>
      <ul>
        <li>IP address, browser type and version, device type, operating system, and language settings</li>
        <li>Pages visited, features used, timestamps, referral URLs, and general interaction data</li>
        <li>Log and hosting metadata from our infrastructure providers</li>
        <li>Marketing and conversion events related to signup, subscriptions, and public marketing pages</li>
      </ul>

      <h3 className="h5 mt-3">Optional integrations</h3>
      <p>If you choose to connect optional integrations, we may store integration tokens and related metadata, including:</p>
      <ul>
        <li>Spotify account connection data when you link Spotify for classroom music features</li>
        <li>Canva account connection data when you link Canva for content creation</li>
      </ul>
      <p>
        The camera widget accesses your device camera locally in the browser to display video during class. We do
        not store camera video on our servers.
      </p>

      <h2 className="h4 mt-4">3. How We Use Information</h2>
      <p>We use the information we collect to:</p>
      <ul>
        <li>Provide, operate, maintain, and improve the Service</li>
        <li>Authenticate users and manage accounts, classes, automations, and subscriptions</li>
        <li>Process payments and school license enrollment</li>
        <li>Respond to support requests and communicate about the Service</li>
        <li>Send service-related notices, such as security updates or account messages</li>
        <li>Measure product usage, diagnose errors, and improve reliability</li>
        <li>Understand marketing performance on public pages and advertising campaigns</li>
        <li>Operate referral program features when you opt in</li>
        <li>Protect the Service, enforce our policies, and comply with legal obligations</li>
      </ul>
      <p>
        We do <strong>not</strong> sell student data. We do <strong>not</strong> use student roster or classroom
        data for targeted advertising.
      </p>

      <h2 className="h4 mt-4">4. Student Data and Educational Use</h2>
      <p>
        Lesson Launchpad supports teachers and schools in managing classroom workflows. Student information in the
        Service is entered and controlled by authorized educators or school staff, not by students creating their
        own accounts.
      </p>
      <ul>
        <li>Student data is used only to provide the educational features you configure.</li>
        <li>Schools and educators retain ownership and control over the classroom data they enter.</li>
        <li>We process student information as a service provider to teachers and schools, not for our own independent educational profiling of students.</li>
        <li>We are willing to execute district Data Privacy Agreements (DPAs) where required. Contact us to discuss school or district procurement needs.</li>
      </ul>
      <p>
        We follow practices aligned with the Family Educational Rights and Privacy Act (FERPA) in our role as a
        service provider to educators and schools.
      </p>

      <h2 className="h4 mt-4">5. Children&apos;s Privacy</h2>
      <p>
        The Service is directed to educators and schools, not to children under 13. We do not knowingly collect
        personal information directly from children under 13.
      </p>
      <p>
        Teachers may enter student names, birthdays, and related classroom information under school or district
        direction. If you believe we have collected personal information from a child under 13 outside that
        authorized context, contact us and we will take appropriate steps to review and delete it.
      </p>
      <p>
        We follow practices aligned with the Children&apos;s Online Privacy Protection Act (COPPA) for our
        educator-facing service model.
      </p>

      <h2 className="h4 mt-4">6. Cookies and Similar Technologies</h2>
      <p>
        We use cookies, local storage, session storage, pixels, tags, and similar technologies to operate the
        Service, remember preferences, keep you signed in, measure usage, and understand marketing performance.
      </p>

      <h3 className="h5 mt-3">Types of technologies we use</h3>
      <ul>
        <li>
          <strong>Essential technologies:</strong> Required for authentication, security, core site functionality,
          and saving account-related preferences.
        </li>
        <li>
          <strong>Analytics technologies:</strong> Help us understand how the Service is used, including Google
          Analytics and Google Tag Manager.
        </li>
        <li>
          <strong>Advertising and attribution technologies:</strong> Help us measure marketing campaigns and
          conversions, including Meta (Facebook) Pixel and Google Ads tags on public and marketing pages.
        </li>
        <li>
          <strong>Product analytics technologies:</strong> When enabled, session analytics tools such as FullStory
          may record interactions on selected routes. Present screens, share links, school enrollment flows, and
          roster-editing routes are excluded from identified session recording. Roster-derived displays, including
          roster boxes and Randomizer results, are blocked from capture.
        </li>
        <li>
          <strong>Error monitoring and debugging:</strong> When enabled, Sentry may capture frontend errors and,
          for error-triggered session replay, a short recording of the moments before an error on allowed routes.
          Admin, billing, account settings, share links, roster-editing routes, and other sensitive routes are
          excluded from replay recording. Roster-derived displays are blocked from capture, and text, inputs, and
          media are masked or blocked by default.
        </li>
      </ul>
      <p>
        You can control cookies through your browser settings. Blocking essential cookies may prevent parts of the
        Service from working properly.
      </p>

      <h2 className="h4 mt-4">7. Third-Party Service Providers</h2>
      <p>
        We use trusted third-party providers to host, operate, analyze, bill, and support the Service. These
        providers process information on our behalf according to their own privacy policies and our agreements
        with them.
      </p>
      <div className="table-responsive trust-center-table-wrap">
        <table className="table table-sm trust-center-table">
          <thead>
            <tr>
              <th>Provider</th>
              <th>Purpose</th>
            </tr>
          </thead>
          <tbody>
            {SUBPROCESSORS.map((subprocessor) => (
              <tr key={subprocessor.provider}>
                <td>{subprocessor.provider}</td>
                <td>{subprocessor.purpose}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p>
        Some pages also load content or scripts from additional services such as Google Fonts, Font Awesome,
        Google Sign-In, and Stripe checkout widgets when those features are displayed.
      </p>

      <h2 className="h4 mt-4">8. How We Share Information</h2>
      <p>We may share information in the following situations:</p>
      <ul>
        <li>
          <strong>With service providers:</strong> To host the Service, process payments, deliver email or support
          workflows, measure analytics, and perform other functions described in this policy.
        </li>
        <li>
          <strong>With schools and authorized administrators:</strong> When you join a school license or when school
          administrators manage enrollment for their organization.
        </li>
        <li>
          <strong>With your direction:</strong> When you use share links, exports, integrations, or other features
          that expose classroom content outside your account.
        </li>
        <li>
          <strong>For legal and safety reasons:</strong> When required by law, valid legal process, or to protect
          rights, safety, and security.
        </li>
        <li>
          <strong>During a business transaction:</strong> Such as a merger, acquisition, financing, or sale of
          assets, subject to appropriate notice where required.
        </li>
      </ul>
      <p>We do not sell personal information or student data.</p>

      <h2 className="h4 mt-4">9. Data Retention</h2>
      <p>
        We retain information for as long as needed to provide the Service, comply with legal obligations, resolve
        disputes, and enforce our agreements.
      </p>
      <ul>
        <li>Active account data is retained while your account remains active.</li>
        <li>
          After subscription cancellation, account data may be retained for up to{" "}
          {RETENTION_MONTHS_AFTER_CANCELLATION} months before deletion, unless a longer retention period is
          required by law or requested by a school contract.
        </li>
        <li>Backup, log, and billing records may be retained for limited additional periods where necessary.</li>
      </ul>

      <h2 className="h4 mt-4">10. Your Choices and Rights</h2>
      <p>Depending on where you live, you may have rights to access, correct, export, or delete personal information.</p>
      <ul>
        <li>
          You can update much of your teacher profile information, email, password, and classroom content from
          within the Service.
        </li>
        <li>
          To request access, correction, export, or deletion of account or classroom data, contact{" "}
          <a href="mailto:support@lessonlaunchpad.com">support@lessonlaunchpad.com</a>.
        </li>
        <li>
          Canceling a subscription stops future billing but does not immediately delete saved classes, rosters,
          content, or other account data.
        </li>
      </ul>
      <p>
        We may retain certain information when we have a legal obligation or other legitimate reason to do so.
      </p>

      <h2 className="h4 mt-4">11. Security</h2>
      <p>
        We use commercially reasonable administrative, technical, and organizational measures designed to protect
        information, including encryption in transit (HTTPS/TLS), encryption at rest through our cloud
        infrastructure, Firebase Authentication, role-based access controls, and Firestore security rules.
      </p>
      <h3 className="h5 mt-3">Data breach notification</h3>
      <p>
        If we discover a data breach that has compromised personally identifiable information, we will notify
        affected schools, districts, and users without unreasonable delay and no later than 48 hours after
        discovery. Notice will be provided to the primary contact associated with the affected account or
        organization and will include available information about the incident, the information involved, and
        recommended protective steps. We will comply with any shorter notification period required by applicable
        law. Notification may be delayed when required by law enforcement or applicable law.
      </p>
      <p>
        No method of transmission or storage is completely secure. We cannot guarantee absolute security.
      </p>

      <h2 className="h4 mt-4">12. International Data Transfers</h2>
      <p>
        We process information in the United States and through service providers that may process data in the
        United States or other countries. If you access the Service from outside the United States, you understand
        that your information may be transferred to and processed in jurisdictions with different data protection
        laws.
      </p>

      <h2 className="h4 mt-4">13. Links to Other Sites</h2>
      <p>
        The Service may contain links to third-party websites or integrations. We are not responsible for the
        privacy practices of those third parties. Review their privacy policies before providing information to
        them.
      </p>

      <h2 className="h4 mt-4">14. Changes to This Privacy Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. We will post the updated policy on this page and
        revise the &quot;Last updated&quot; date above. If changes are material, we may provide additional notice
        through the Service or by email where appropriate.
      </p>

      <h2 className="h4 mt-4">15. Contact Us</h2>
      <p>If you have questions about this Privacy Policy or our privacy practices, contact us:</p>
      <ul>
        <li>
          Email:{" "}
          <a href="mailto:support@lessonlaunchpad.com">support@lessonlaunchpad.com</a>
        </li>
        <li>
          Security inquiries:{" "}
          <a href="mailto:security@lessonlaunchpad.com">security@lessonlaunchpad.com</a>
        </li>
        <li>
          Contact form: <Link to="/contact">https://lessonlaunchpad.com/contact</Link>
        </li>
      </ul>
    </article>
  );
}
